import { Spin } from "antd";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  ISurvey,
  ISurveyAnswer,
  ISurveyQuestion,
  IQuestionWithConditionalRecord,
} from "../../../../../@types/Surveys";
import { Button } from "../../../../../components/Button";
import { ProgressBar } from "../../../../../components/ProgressBar";
import { readRequestedLanguage } from "../../../../../config/i18n";
import { useDynamicsurveys } from "../../../../../hooks/useDynamicSurveys";
import { surveysService } from "../../../../../services/surveysService";
import { translationService } from "../../../../../services/translationsService";
import { applyWebViewLanguage } from "../../../../../utils/languageUtils";
import { parsePaths } from "../../../../../utils/surveyMediaAnswer";
import { hasSurveyAnswerValueChanged } from "../../../../../utils/surveysUtils";
import { parseSurveyTranslationResponseToDisplay } from "../../../../../utils/translationsUtils";
import { DateQuestionDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/DateQuestionDisplay";
import { FiveQuestionsAboutSupOxDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/FiveQuestionsAboutSupOxDisplay";
import { InformativeQuestionDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/InformativeQuestionDisplay";
import { MediaQuestionDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/MediaQuestionDisplay";
import { MultiSelectQuestionDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/MultiSelectQuestionDisplay";
import { SingleSelectQuestionDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/SingleSelectQuestionDisplay";
import { SliderQuestionDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/SliderQuestionDisplay";
import { TextQuestionDisplay } from "../../../SurveyBuilder/Components/ContentTab/Components/TextQuestionDisplay";
import {
  SurveyDescription,
  SubmitContainer,
  SectionIterationButton,
  FormAnswerContainer,
  QuestionsAnswerContainer,
  spinCss,
  buttonCss,
  DisplayQuestion,
  FormInfoContainer,
  FormTitle,
  ProgressBarContainer,
  InfoContainer,
  buttonCssExtraMarginBottom,
} from "./AnswerSurvey.styles";
import {
  AddMediaButtons,
  IPendingUpload,
} from "./Components/AddMediaButtons/AddMediaButtons";

type AnswerSurveyType = {
  clinicianUserSurveyId?: string;
  surveyAnswersToEdit?: ISurveyAnswer[];
  surveyAnswerId?: string;
  surveyId?: string;
  patientUserId?: number;
  onClinicianFormSubmit?: (responseStatus?: number) => void;
};

export function AnswerSurvey({
  clinicianUserSurveyId,
  patientUserId,
  surveyAnswersToEdit,
  surveyId,
  surveyAnswerId,
  onClinicianFormSubmit,
}: AnswerSurveyType) {
  const [selectedQuestionMinimumIndex, setSelectedQuestionMinimumIndex] =
    useState<number>(-1);

  const [selectedQuestionIndex, setSelectedQuestionIndex] =
    useState<number>(-1);

  const [survey, setSurvey] = useState<ISurvey>();
  const [isFiveQuestionsValidated, setIsFiveQuestionsValidated] =
    useState<boolean>(false);

  const [canModifySingleSelect, setCanModifySingleSelect] =
    useState<boolean>(true);

  const [questionIdListToDisplay, setQuestionIdListToDisplay] = useState<
    IQuestionWithConditionalRecord[]
  >([]);
  const [displaySurveyQuestions, setDisplaySurveyQuestions] =
    useState<ISurveyQuestion[]>();

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingTranslations, setLoadingTranslations] = useState<boolean>(true);
  const [pendingUploads, setPendingUploads] = useState<IPendingUpload[]>([]);
  const isAnythingUploading = pendingUploads.length > 0;
  const [mediaBlobUrls, setMediaBlobUrls] = useState<Record<string, string>>(
    {}
  );
  const mediaBlobUrlsRef = useRef(mediaBlobUrls);
  useEffect(() => {
    mediaBlobUrlsRef.current = mediaBlobUrls;
  }, [mediaBlobUrls]);
  useEffect(() => {
    return () => {
      Object.values(mediaBlobUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  const forgetMediaBlobUrl = (path: string) => {
    setMediaBlobUrls((prev) => {
      const url = prev[path];
      if (url) URL.revokeObjectURL(url);
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };
  const [leftPosition, setLeftPosition] = useState(0);

  const [displayProgressBar, setDisplayProgressBar] = useState<boolean>(true);
  const [answeredPercentage, setAnsweredPercentage] = useState<number>(0);
  const [imageUploadError, setImageUploadError] = useState<string>();
  const [blurryPaths, setBlurryPaths] = useState<Record<string, true>>({});

  const markPathAsBlurry = (path: string) => {
    setBlurryPaths((prev) => ({ ...prev, [path]: true }));
  };

  const forgetBlurryPath = (path: string) => {
    setBlurryPaths((prev) => {
      if (!prev[path]) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [userSurveyId, setUserSurveyId] = useState<string>();
  const {
    surveyAnswerArray,
    setValueSurveyAnswerArray,
    questionWithConditionalList,
    setQuestionWithConditionalList,
    updateSurveyAnswerArray,
  } = useDynamicsurveys();

  const [searchParams] = useSearchParams();
  // EN-5289: set when staff open a survey as the patient (Surveys > Answer).
  // It goes on each request, never into tokenResponse / isMobileApp, so the
  // staff session survives. The app's own webview stores its token itself and
  // never passes one in the URL.
  const patientToken = searchParams.get("token") ?? undefined;

  const { t, i18n } = useTranslation();

  const resolvedPatientUserId = useMemo(() => {
    if (patientUserId) return patientUserId;

    const fromUrl = searchParams.get("patientUserId");
    return fromUrl ? parseInt(fromUrl, 10) : undefined;
  }, [patientUserId, searchParams]);

  const isLastQuestion = useMemo(() => {
    if (!displaySurveyQuestions || selectedQuestionIndex === -1) return false;
    if (selectedQuestionIndex < displaySurveyQuestions.length - 1) return false;

    const currentQuestion = displaySurveyQuestions[selectedQuestionIndex];

    const isUnresolvedConditional = questionWithConditionalList.some(
      (q) => q.questionId === currentQuestion?.id && q.jumpTo === ""
    );

    return !isUnresolvedConditional;
  }, [
    displaySurveyQuestions,
    selectedQuestionIndex,
    questionWithConditionalList,
  ]);

  const isForms =
    clinicianUserSurveyId !== undefined || surveyAnswerId !== undefined;

  const handleTabClick = useCallback((e: KeyboardEvent) => {
    if (e.code === "Tab") {
      e.preventDefault();
    }
  }, []);

  const fetchSurvey = async ({
    userSurveyId,
    surveyId,
  }: {
    userSurveyId?: string;
    surveyId?: string;
  }) => {
    try {
      setLoading(true);

      let response;

      if (userSurveyId) {
        response = await surveysService.getSurveyByUserSurveyIdMobile(
          userSurveyId,
          patientToken
        );
      } else if (surveyId) {
        response = await surveysService.getSurveyById(surveyId);
      } else {
        return;
      }

      const { data, status } = response;

      if (status >= 200 && status < 300) {
        setSurvey(data);

        if (data?.description?.replace(/ /g, "") === "") {
          setSelectedQuestionMinimumIndex(0);
          setSelectedQuestionIndex(0);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleTabClick, false);

    const initializeSurvey = async () => {
      let resolvedUserSurveyId: string | undefined;
      let resolvedSurveyId: string | undefined;

      if (clinicianUserSurveyId !== undefined) {
        resolvedUserSurveyId = clinicianUserSurveyId;
        setUserSurveyId(clinicianUserSurveyId);
      } else if (surveyAnswerId && surveyId) {
        resolvedSurveyId = surveyId;
      } else {
        resolvedUserSurveyId = window.location.pathname.split("/").pop();
        setUserSurveyId(resolvedUserSurveyId);
      }

      fetchSurvey({
        userSurveyId: resolvedUserSurveyId,
        surveyId: resolvedSurveyId,
      });

      // One tag for both the request and the bundle we register under: reading
      // i18n.language back out is what let those two drift apart and left the
      // navigation buttons showing their translation keys (EN-5224).
      const languageCode = await applyWebViewLanguage(
        i18n,
        readRequestedLanguage()
      );

      try {
        const translations =
          await translationService.getSurveysFixedTranslationsByLanguageCode(
            languageCode
          );

        if (Array.isArray(translations?.data)) {
          const resourcesToAdd = parseSurveyTranslationResponseToDisplay(
            translations.data
          );
          i18n.addResourceBundle(languageCode, "translation", resourcesToAdd);
        }
      } catch {
        // A failed fetch must not leave the patient on a spinner: i18next falls
        // back to the default language, which still beats a blocked survey.
      } finally {
        setLoadingTranslations(false);
      }
    };

    initializeSurvey();

    return () => {
      window.removeEventListener("keydown", handleTabClick, false);
    };
  }, []);

  useEffect(() => {
    if (!survey) return;

    const isMobileApp = window.localStorage.getItem("isMobileApp");
    if (!isMobileApp) return;

    const hasMediaQuestion = survey.sections.some((section) =>
      section.questions.some((question) => question.type === "Media")
    );

    if (hasMediaQuestion) {
      (window as { [key: string]: any }).requestCameraPermissionFromFlutter();
    }
  }, [survey]);

  useEffect(() => {
    let questionCount = 0;
    const updatedSurveyAnswersArray: ISurveyAnswer[] = [
      ...(surveyAnswersToEdit ?? []),
    ];
    const firstQuestionWithConditionalList: IQuestionWithConditionalRecord[] =
      [];

    survey?.sections.forEach((section) => {
      section.questions.forEach((question) => {
        questionCount += 1;
        if (question.id) {
          if (!surveyAnswersToEdit) {
            updatedSurveyAnswersArray.push({
              surveyQuestionId: question.id,
              answerValue: "",
            });
          }

          if (question.type === "SingleSelect") {
            question.options?.forEach((option) => {
              if (option.nextQuestionId) {
                if (
                  firstQuestionWithConditionalList.find(
                    (questionWithconditional) =>
                      questionWithconditional.questionId === question.id
                  ) === undefined
                ) {
                  firstQuestionWithConditionalList.push({
                    questionId: question.id,
                    jumpTo: "",
                  });
                }
              }
            });
          }
        }
      });
    });

    setQuestionWithConditionalList(firstQuestionWithConditionalList);
    setValueSurveyAnswerArray(updatedSurveyAnswersArray);
    setDisplayProgressBar(questionCount > 1);
  }, [survey]);

  const validateQuestionAnswer = () => {
    if (selectedQuestionIndex !== -1) {
      if (displaySurveyQuestions) {
        const currentQuestion = displaySurveyQuestions[selectedQuestionIndex];

        if (currentQuestion?.type === "Media" && isAnythingUploading) {
          return false;
        }

        if (
          currentQuestion?.type === "Informative" ||
          currentQuestion?.mandatory === false
        ) {
          return true;
        }
        if (currentQuestion?.type === "Media") {
          const answerValue = surveyAnswerArray?.find(
            (surveyAnswer) =>
              surveyAnswer.surveyQuestionId === currentQuestion?.id
          )?.answerValue;
          return parsePaths(answerValue).length > 0;
        }
        if (currentQuestion?.type !== "FiveQuestionsAboutSupOx") {
          return (
            surveyAnswerArray?.find(
              (surveyAnswer) =>
                surveyAnswer.surveyQuestionId === currentQuestion?.id
            )?.answerValue !== ""
          );
        }
        return isFiveQuestionsValidated;
      }
    }
    return true;
  };

  const getAnsweredPercentage = () => {
    const currentQuestion = questionIdListToDisplay[selectedQuestionIndex];
    if (selectedQuestionIndex === -1 || !currentQuestion) {
      return;
    }

    const questionsAnswered = surveyAnswerArray.findIndex(
      (surveyAnswer) =>
        surveyAnswer.surveyQuestionId === currentQuestion.questionId
    );

    if (questionsAnswered)
      setAnsweredPercentage(
        Math.round((questionsAnswered * 100) / surveyAnswerArray.length)
      );
  };

  const getQuestionIdToJumpAccordingToAnswerFromSurvey = (
    answer: ISurveyAnswer
  ): string => {
    let valueToReturn = "";
    survey?.sections.forEach((section) => {
      section.questions.forEach((question) => {
        if (question.id === answer.surveyQuestionId) {
          question.options?.forEach((option) => {
            if (
              answer.surveyQuestionOptionIds?.find(
                (surveyQuestionOptionId) => surveyQuestionOptionId === option.id
              )
            ) {
              valueToReturn = option.nextQuestionId ?? "";
            }
          });
        }
      });
    });
    return valueToReturn;
  };

  const handleQuestionAnimation = (moveQuestion: "forward" | "back") => {
    const windowWidth = window.innerWidth;
    if (selectedQuestionIndex !== -1) {
      switch (true) {
        case windowWidth < 769:
          if (moveQuestion === "forward") {
            setLeftPosition(leftPosition - window.innerWidth + 20);
          } else {
            setLeftPosition(leftPosition + window.innerWidth - 20);
          }
          break;
        case windowWidth > 768 && windowWidth < 1681:
          if (moveQuestion === "forward") {
            setLeftPosition(leftPosition - 0.8 * window.innerWidth);
          } else {
            setLeftPosition(leftPosition + 0.8 * window.innerWidth);
          }
          break;

        default:
          if (moveQuestion === "forward") {
            setLeftPosition(leftPosition - 0.5 * window.innerWidth);
          } else {
            setLeftPosition(leftPosition + 0.5 * window.innerWidth);
          }
          break;
      }
    }
  };

  const handleWindowResize = () => {
    const windowWidth = window.innerWidth;
    if (selectedQuestionIndex !== -1) {
      if (windowWidth < 769)
        setLeftPosition(0 - selectedQuestionIndex * (window.innerWidth - 20));
      if (windowWidth > 768 && windowWidth < 1681)
        setLeftPosition(0 - selectedQuestionIndex * (0.8 * window.innerWidth));
      if (windowWidth > 1680)
        setLeftPosition(0 - selectedQuestionIndex * (0.5 * window.innerWidth));
    }
  };

  window.addEventListener("resize", (e) => {
    e.stopPropagation();
    handleWindowResize();
  });

  const handleNextPageClick = () => {
    const newSelectedSectionIndex = selectedQuestionIndex + 1;
    setSelectedQuestionIndex(newSelectedSectionIndex);
    setCanModifySingleSelect(true);
    handleQuestionAnimation("forward");
  };

  const handlePreviousPageClick = () => {
    const newSelectedSectionIndex = selectedQuestionIndex - 1;
    setSelectedQuestionIndex(newSelectedSectionIndex);
    if (selectedQuestionIndex !== 0) handleQuestionAnimation("back");
  };

  const getQuestionIdListToDisplayFromConditionalJump = () => {
    const questionIdListToDisplay: IQuestionWithConditionalRecord[] = [];
    let skip = false;
    let changeSkipId = "";

    surveyAnswerArray.forEach((surveyAnswer, surveyAnswerIndex) => {
      if (changeSkipId === surveyAnswer.surveyQuestionId) {
        skip = false;
      }
      if (!skip) {
        const questionHasConditional = questionWithConditionalList.find(
          (questionWithconditional) =>
            questionWithconditional.questionId === surveyAnswer.surveyQuestionId
        );
        if (questionHasConditional) {
          const nextId = questionHasConditional.jumpTo;
          questionIdListToDisplay.push({
            questionId: surveyAnswer.surveyQuestionId,
            jumpTo: nextId,
          });
          skip = true;
          changeSkipId = nextId;
        } else if (surveyAnswerIndex + 1 < surveyAnswerArray.length) {
          let question: ISurveyQuestion = {
            id: "string",
            order: 0,
            text: "string",
            description: "string",
            type: "Text",
            mandatory: true,
          };
          survey?.sections.forEach((surveySection) => {
            const questionToFind = surveySection.questions.find(
              (surveyQuestion) =>
                surveyQuestion.id ===
                surveyAnswerArray[surveyAnswerIndex].surveyQuestionId
            );
            if (questionToFind) {
              question = questionToFind;
            }
          });
          if (question?.nextQuestionId) {
            questionIdListToDisplay.push({
              questionId: surveyAnswer.surveyQuestionId,
              jumpTo: question?.nextQuestionId,
            });
            skip = true;
            changeSkipId = question?.nextQuestionId;
          } else {
            questionIdListToDisplay.push({
              questionId: surveyAnswer.surveyQuestionId,
              jumpTo: surveyAnswerArray[surveyAnswerIndex + 1].surveyQuestionId,
            });
          }
        } else {
          questionIdListToDisplay.push({
            questionId: surveyAnswer.surveyQuestionId,
            jumpTo: "",
          });
        }
      }
    });
    setQuestionIdListToDisplay(questionIdListToDisplay);
  };

  useEffect(() => {
    const updateQuestionWithConditionalList = [...questionWithConditionalList];
    let resetJumpingLogic = false;
    questionWithConditionalList?.forEach(
      (questionWithConditional, questionWithConditionalIndex) => {
        const currentAnswer = surveyAnswerArray.find(
          (answer) =>
            answer.surveyQuestionId === questionWithConditional.questionId
        );
        if (currentAnswer) {
          const currentAnswerJump =
            getQuestionIdToJumpAccordingToAnswerFromSurvey(currentAnswer);

          if (currentAnswer.answerValue !== "") {
            if (resetJumpingLogic) {
              updateQuestionWithConditionalList[
                questionWithConditionalIndex
              ].jumpTo = "";
            } else if (currentAnswerJump !== "") {
              updateQuestionWithConditionalList[
                questionWithConditionalIndex
              ].jumpTo = currentAnswerJump;
            } else if (currentAnswerJump === "") {
              const nextQuestionId =
                surveyAnswerArray[
                  surveyAnswerArray.findIndex(
                    (answer) =>
                      answer.surveyQuestionId === currentAnswer.surveyQuestionId
                  ) + 1
                ].surveyQuestionId;
              updateQuestionWithConditionalList[
                questionWithConditionalIndex
              ].jumpTo = nextQuestionId;
            }
          } else {
            updateQuestionWithConditionalList[
              questionWithConditionalIndex
            ].jumpTo = "";

            if (
              displaySurveyQuestions?.find(
                (question) => question.id === currentAnswer.surveyQuestionId
              )
            ) {
              resetJumpingLogic = true;
            }
          }
        }
      }
    );
    setQuestionWithConditionalList(updateQuestionWithConditionalList);
    getQuestionIdListToDisplayFromConditionalJump();
  }, [surveyAnswerArray]);

  const checkIfSingleSelectIsLastQuestion = (): boolean => {
    let result = false;
    const lastDisplayQuestion = displaySurveyQuestions?.pop();
    if (lastDisplayQuestion?.type === "SingleSelect") {
      lastDisplayQuestion.options?.forEach((option) => {
        if (option.nextQuestionId === undefined) {
          result = true;
        }
      });
    }
    return result;
  };

  const updateDisplaySurveyArray = () => {
    const updateDisplaySurveysQuestions: ISurveyQuestion[] = [];

    survey?.sections.forEach((section) => {
      section.questions.forEach((question) => {
        if (
          questionIdListToDisplay.find(
            (questionIdToDisplay) =>
              questionIdToDisplay.questionId === question.id
          )
        ) {
          updateDisplaySurveysQuestions.push(question);
        }
      });
    });
    setDisplaySurveyQuestions(updateDisplaySurveysQuestions);
    if (
      validateQuestionAnswer() &&
      displaySurveyQuestions &&
      selectedQuestionIndex !== -1 &&
      displaySurveyQuestions[selectedQuestionIndex]?.type === "SingleSelect" &&
      (selectedQuestionIndex < displaySurveyQuestions.length - 1 ||
        !checkIfSingleSelectIsLastQuestion())
    ) {
      setCanModifySingleSelect(false);
      setTimeout(() => {
        handleNextPageClick();
      }, 300);
    }
  };

  useEffect(() => {
    updateDisplaySurveyArray();
    getAnsweredPercentage();
  }, [questionIdListToDisplay]);

  useEffect(() => {
    if (
      displaySurveyQuestions &&
      selectedQuestionIndex !== -1 &&
      displaySurveyQuestions[selectedQuestionIndex]?.type === "Informative"
    ) {
      updateSurveyAnswerArray({
        surveyQuestionId:
          displaySurveyQuestions[selectedQuestionIndex]?.id ?? "",
        answerValue: "no_answer",
      });
    }
    getAnsweredPercentage();
  }, [selectedQuestionIndex]);

  const getAsnwerArray = () => {
    const updatedAnswerArray: ISurveyAnswer[] = [];
    surveyAnswerArray.forEach((answer) => {
      if (answer.answerValue !== "")
        updatedAnswerArray.push({
          surveyQuestionId: answer.surveyQuestionId,
          answerValue: answer.answerValue,
          surveyQuestionOptionIds: answer.surveyQuestionOptionIds,
        } as ISurveyAnswer);
    });

    return updatedAnswerArray;
  };

  const handleSubmit = async () => {
    const answerArray = getAsnwerArray();
    let response;

    if (userSurveyId) {
      setSubmitLoading(true);

      response = await surveysService.postUserSurveyAnswersMobile(
        userSurveyId,
        answerArray,
        resolvedPatientUserId,
        patientToken
      );
    } else if (surveyAnswerId) {
      setSubmitLoading(true);
      if (
        surveyAnswersToEdit &&
        hasSurveyAnswerValueChanged(surveyAnswersToEdit, answerArray)
      ) {
        response = await surveysService.updateSurveyAnswer(
          surveyAnswerId,
          answerArray
        );
      }
    } else {
      return;
    }

    if (onClinicianFormSubmit) {
      onClinicianFormSubmit(response?.status);
      return;
    }

    if (response) {
      if (response.status >= 200 && response.status < 300) {
        (window as { [key: string]: any }).sendPostStatusToFlutter(
          Boolean(true)
        );
      } else {
        (window as { [key: string]: any }).sendPostStatusToFlutter(
          Boolean(false)
        );
      }
    }
    setSubmitLoading(false);
  };

  return loading || loadingTranslations ? (
    <Spin rootClassName={spinCss()} fullscreen size="large" />
  ) : (
    <FormAnswerContainer
      isForms={isForms}
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      {survey !== undefined &&
        displaySurveyQuestions &&
        displaySurveyQuestions.length > 0 && (
          <>
            {selectedQuestionIndex === -1 && (
              <FormInfoContainer isForms={isForms}>
                <InfoContainer>
                  <FormTitle>{survey.name}</FormTitle>
                  <SurveyDescription>{survey.description}</SurveyDescription>
                </InfoContainer>
                <Button
                  type="button"
                  label={`${t(
                    "survey_fixed_translations_survey_navigation_start"
                  )}`}
                  onClick={() => handleNextPageClick()}
                  className={buttonCss()}
                  disabled={!validateQuestionAnswer()}
                />
              </FormInfoContainer>
            )}

            {selectedQuestionIndex > -1 && (
              <QuestionsAnswerContainer
                isForms={isForms}
                style={{ left: leftPosition }}
              >
                {displaySurveyQuestions.map((question: ISurveyQuestion) => (
                  <React.Fragment key={`${question.id}`}>
                    <DisplayQuestion isForms={isForms}>
                      {(question.type === "Text" ||
                        question.type === "MultilineText") && (
                        <TextQuestionDisplay
                          key={`${question.id}`}
                          questionId={question.id}
                          title={question.text}
                          type={question.type}
                          description={question.description}
                          isForms={isForms}
                        />
                      )}
                      {question.type === "SingleSelect" && (
                        <SingleSelectQuestionDisplay
                          key={`${question.id}`}
                          questionId={question.id}
                          title={question.text}
                          description={question.description}
                          options={question.options}
                          canModify={canModifySingleSelect}
                          isForms={isForms}
                        />
                      )}
                      {question.type === "MultiSelect" && (
                        <MultiSelectQuestionDisplay
                          key={`${question.id}`}
                          questionId={question.id}
                          title={question.text}
                          description={question.description}
                          options={question.options}
                          isForms={isForms}
                        />
                      )}
                      {(question.type === "Slider" ||
                        question.type === "SliderVas") &&
                        question.attributes && (
                          <SliderQuestionDisplay
                            verticalSliderHeight="100%"
                            key={`${question.id}`}
                            questionId={question.id}
                            title={question.text}
                            description={question.description}
                            options={question.options}
                            step={parseFloat(
                              question.attributes.find(
                                (attribute) => attribute?.name === "Step"
                              )?.value
                            )}
                            orientation={
                              question.attributes.find(
                                (attribute) => attribute?.name === "Orientation"
                              )?.value
                            }
                            isForms={isForms}
                          />
                        )}

                      {question.type === "Date" && (
                        <DateQuestionDisplay
                          key={`${question.id}`}
                          questionId={question.id}
                          title={question.text}
                          description={question.description}
                          highZIndex={isForms}
                          isForms={isForms}
                        />
                      )}
                      {question.type === "Media" && (
                        <MediaQuestionDisplay
                          key={`${question.id}`}
                          questionId={question.id}
                          title={question.text}
                          description={question.description}
                          isForms={isForms}
                          error={imageUploadError}
                          clearError={() => setImageUploadError(undefined)}
                          pendingUploads={pendingUploads}
                          mediaBlobUrls={mediaBlobUrls}
                          forgetMediaBlobUrl={forgetMediaBlobUrl}
                          blurryPaths={blurryPaths}
                          forgetBlurryPath={forgetBlurryPath}
                        />
                      )}
                      {question.type === "FiveQuestionsAboutSupOx" && (
                        <FiveQuestionsAboutSupOxDisplay
                          key={`${question.id}`}
                          questionId={question.id}
                          setIsFiveQuestionsValidated={
                            setIsFiveQuestionsValidated
                          }
                          isForms={isForms}
                        />
                      )}
                      {question.type === "Informative" && (
                        <InformativeQuestionDisplay
                          key={`${question.id}`}
                          HTMLText={question.text}
                          isForms={isForms}
                        />
                      )}
                      <SubmitContainer>
                        {displayProgressBar && (
                          <ProgressBarContainer>
                            <ProgressBar percent={answeredPercentage} />
                          </ProgressBarContainer>
                        )}

                        {displaySurveyQuestions[selectedQuestionIndex]?.type ===
                        "Media" ? (
                          <AddMediaButtons
                            setUploadErrorMessage={setImageUploadError}
                            markPathAsBlurry={markPathAsBlurry}
                            key={`${question.id}`}
                            questionId={question.id}
                            isLastQuestion={isLastQuestion}
                            isMandatory={
                              displaySurveyQuestions[selectedQuestionIndex]
                                ?.mandatory
                            }
                            userSurveyId={userSurveyId}
                            surveyAnswerId={surveyAnswerId}
                            patientToken={patientToken}
                            handleNextPageClick={handleNextPageClick}
                            handleSubmit={handleSubmit}
                            pendingUploads={pendingUploads}
                            setPendingUploads={setPendingUploads}
                            setMediaBlobUrls={setMediaBlobUrls}
                          />
                        ) : (
                          <Button
                            isLoading={submitLoading}
                            type="button"
                            label={
                              isLastQuestion
                                ? `${t(
                                    "survey_fixed_translations_survey_navigation_submit"
                                  )}`
                                : `${t(
                                    "survey_fixed_translations_survey_navigation_next"
                                  )}`
                            }
                            onClick={() => {
                              if (isLastQuestion) {
                                handleSubmit();
                              } else {
                                handleNextPageClick();
                              }
                            }}
                            className={
                              selectedQuestionIndex >
                              selectedQuestionMinimumIndex
                                ? buttonCss()
                                : buttonCssExtraMarginBottom()
                            }
                            disabled={!validateQuestionAnswer()}
                          />
                        )}
                        {selectedQuestionIndex >
                          selectedQuestionMinimumIndex && (
                          <SectionIterationButton
                            onClick={
                              !isAnythingUploading
                                ? handlePreviousPageClick
                                : undefined
                            }
                            disabled={isAnythingUploading}
                          >
                            {`${t(
                              "survey_fixed_translations_survey_navigation_previous"
                            )}`}
                          </SectionIterationButton>
                        )}
                      </SubmitContainer>
                    </DisplayQuestion>
                  </React.Fragment>
                ))}
              </QuestionsAnswerContainer>
            )}
          </>
        )}
      {survey === undefined && <div>{t("surveys_answer_loadError")}</div>}
    </FormAnswerContainer>
  );
}
